import { HStack, PageContainer, PageHeader, Spread, VStack } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { Filter } from '@/components/DataTable/FilterControls'
import { ExportCsvButton } from '@/components/ExportCsv/ExportCsvButton'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { Note } from '@/components/Note'
import { useCrtPeriodSync } from '@/hooks/crt/useCrtPeriodSync'
import useBrandFilter from '@/hooks/filters/useBrandFilter'
import useMaterialGroupFilter from '@/hooks/filters/useMaterialGroupFilter'
import useMaterialMarkingFilter from '@/hooks/filters/useMaterialMarkingFilter'
import usePackContentSizeFilter from '@/hooks/filters/usePackContentSizeFilter'
import useProductCategoryFilter from '@/hooks/filters/useProductCategoryFilter'
import useProductTypeFilter from '@/hooks/filters/useProductTypeFilter'
import useVariantFilter from '@/hooks/filters/useVariantFilter'
import useVolumeForecastStatusFilter from '@/hooks/filters/useVolumeForecastStatusFilter'
import { useCurrentChannelContributions } from '@/hooks/queries/crt/useChannelContributions'
import { useCurrentNcProvincialContributions } from '@/hooks/queries/crt/useNcProvincialContributions'
import {
  useVolumeForecasts,
  volumeForecastsExportQueryOptions,
  volumeForecastsSearchSchema,
} from '@/hooks/queries/crt/useVolumeForecasts'
import { useLocalizedDate } from '@/hooks/useLocalizedDate'
import { useTablePagination } from '@/hooks/useTablePagination'
import { getClearedFilters } from '@/lib/utils/filters'
import {
  INDETERMINATE,
  MONTH_KEYS,
  VOL_CORRECTED_FORECAST_STATUSES,
  YEAR_FLAGS,
} from '@repo/constants'
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
} from '@repo/ui/components'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { format } from 'date-fns'
import { AlertTriangleIcon, ExternalLinkIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { VolumeForecastsConfigurationMenu } from './-components/ConfigurationMenu'
import { VolumeForecastsCacheState } from './-components/VolumeForecastsCacheState'
import { VolumeForecastsDataTable } from './-components/VolumeForecastsDataTable'

type VolumeForecastsMode = 'material' | 'fmc' | 'vapour' | 'nrt' | 'materialGroup'

type YearSuffix = 'CY' | 'NY'

const YEAR_SUFFIXES: YearSuffix[] = ['CY', 'NY']

type FilterName =
  | 'productCategory'
  | 'materialGroupCode'
  | 'brand'
  | 'variant'
  | 'packContentSize'
  | 'productType'
  | 'marking'
  | 'status'

const FILTER_NAMES_BY_MODE: Record<VolumeForecastsMode, FilterName[]> = {
  material: [
    'productCategory',
    'materialGroupCode',
    'brand',
    'variant',
    'packContentSize',
    'productType',
    'marking',
    'status',
  ],
  fmc: ['materialGroupCode', 'brand', 'variant', 'packContentSize', 'marking', 'status'],
  vapour: ['materialGroupCode', 'brand', 'variant', 'productType', 'marking', 'status'],
  nrt: ['materialGroupCode', 'brand', 'variant', 'productType', 'marking', 'status'],
  materialGroup: ['materialGroupCode'],
}

export const Route = createFileRoute('/_auth/_modules/crt/volume-forecasts/')({
  component: RouteComponent,
  validateSearch: (search) => volumeForecastsSearchSchema.parse(search),
})

function RouteComponent() {
  const { t } = useTranslation('crt')
  const { formatMonth } = useLocalizedDate()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const queryClient = useQueryClient()

  const { pageSize, onPageSizeChange } = useTablePagination({ navigate, search })

  const now = new Date()

  const { crtPeriod: selectedCrtPeriodStr, selectedCrtPeriodDate } = useCrtPeriodSync({
    search,
    navigate: (search) => navigate({ search }),
  })

  const selectedMonth: number | typeof INDETERMINATE = search.month ?? now.getMonth() + 1
  const selectedYear: YearSuffix = search.year ?? YEAR_FLAGS.CY

  const baseYear = selectedCrtPeriodDate.getFullYear() + (selectedYear === YEAR_FLAGS.NY ? 1 : 0)
  const selectedPeriod: string | undefined =
    selectedMonth === INDETERMINATE
      ? undefined
      : format(new Date(baseYear, selectedMonth - 1, 1), 'yyyy-MM')

  const mode = search.mode ?? 'material'

  const { productCategoryFilter, productCategories, setProductCategories } =
    useProductCategoryFilter(search.productCategory)
  const { materialGroupFilter, materialGroups, setMaterialGroups } = useMaterialGroupFilter(
    search.materialGroupCode,
  )
  const { brandFilter, brands, setBrands } = useBrandFilter(search.brand)
  const { variantFilter, variants, setVariants } = useVariantFilter(search.variant)
  const { packContentSizeFilter, packContentSizes, setPackContentSizes } = usePackContentSizeFilter(
    search.packContentSize,
  )
  const { productTypeFilter, productTypes, setProductTypes } = useProductTypeFilter(
    search.productType,
  )
  const { markingFilter, markings, setMarkings } = useMaterialMarkingFilter(search.marking)
  const { statusFilter, status, setStatus } = useVolumeForecastStatusFilter(search.status)
  const [keyword, setKeyword] = useState<string>(search.keyword ?? '')

  const selectedFilters = {
    productCategory: productCategories?.length ? productCategories : undefined,
    materialGroupCode: materialGroups?.length ? materialGroups : undefined,
    brand: brands?.length ? brands : undefined,
    variant: variants?.length ? variants : undefined,
    packContentSize: packContentSizes?.length ? packContentSizes : undefined,
    productType: productTypes?.length ? productTypes : undefined,
    marking: markings?.length ? markings : undefined,
    status: status.length ? status : undefined,
  }

  // Only the filters listed for the active mode are rendered; the rest stay
  // instantiated (rules of hooks) but hidden. Mode change clears all values.
  const filterByName: Record<FilterName, Filter<unknown>> = {
    productCategory: productCategoryFilter as Filter<unknown>,
    materialGroupCode: materialGroupFilter as Filter<unknown>,
    brand: brandFilter as Filter<unknown>,
    variant: variantFilter as Filter<unknown>,
    packContentSize: packContentSizeFilter as Filter<unknown>,
    productType: productTypeFilter as Filter<unknown>,
    marking: markingFilter as Filter<unknown>,
    status: statusFilter as Filter<unknown>,
  }
  const filters = FILTER_NAMES_BY_MODE[mode].map((name) => filterByName[name])

  const volumeForecastsSearch = {
    crtPeriod: selectedCrtPeriodStr,
    month: selectedMonth,
    year: selectedYear,
    mode,
    productCategory: search.productCategory,
    materialGroupCode: search.materialGroupCode,
    brand: search.brand,
    variant: search.variant,
    packContentSize: search.packContentSize,
    productType: search.productType,
    marking: search.marking,
    keyword: search.keyword,
    status: search.status,
    page: search.page ?? 1,
    pageSize: pageSize,
    sort: search.sort,
  }
  const volumeForecastsQuery = useVolumeForecasts(volumeForecastsSearch)
  const rowCount = volumeForecastsQuery.data?.rowCount ?? 0
  const incompleteUnreleasedMaterialsCount =
    volumeForecastsQuery.data?.incompleteUnreleasedMaterialsCount ?? 0
  const materialsMissingProvincialVolumesCount =
    volumeForecastsQuery.data?.materialsMissingProvincialVolumesCount ?? 0
  const cacheStatus = volumeForecastsQuery.data?.cacheStatus

  const { data: currentChannelContribution } = useCurrentChannelContributions(selectedCrtPeriodStr)
  const { data: currentNcProvincialContribution } =
    useCurrentNcProvincialContributions(selectedCrtPeriodStr)
  const channelContributionsExist = (currentChannelContribution?.rowCount ?? 0) > 0
  const ncProvincialContributionsExist = (currentNcProvincialContribution?.rowCount ?? 0) > 0

  // An unusable snapshot yields no rows, so the table is replaced by the recompute control rather than
  // showing an empty grid with no explanation — but only once the response has arrived and only while both
  // contribution files are in place. Without them there is nothing to recompute, so the table is left
  // empty and the header's period data issues name the missing import.
  const showCacheState =
    cacheStatus !== undefined &&
    !(cacheStatus.status === VOL_CORRECTED_FORECAST_STATUSES.COMPLETED && !cacheStatus.isStale) &&
    channelContributionsExist &&
    ncProvincialContributionsExist

  const handleMonthChange = (value: string) => {
    const nextMonth = value === INDETERMINATE ? INDETERMINATE : Number(value)
    navigate({ search: { ...search, month: nextMonth, page: 1 } })
  }

  const handleYearChange = (value: YearSuffix) => {
    navigate({ search: { ...search, year: value, page: 1 } })
  }

  const clearAllFilterState = () => {
    setProductCategories([])
    setMaterialGroups([])
    setBrands([])
    setVariants([])
    setPackContentSizes([])
    setProductTypes([])
    setMarkings([])
    setStatus([])
  }

  const handleModeChange = (value: string) => {
    const nextMode =
      value === 'material' ||
      value === 'fmc' ||
      value === 'vapour' ||
      value === 'nrt' ||
      value === 'materialGroup'
        ? value
        : 'material'
    clearAllFilterState()
    navigate({
      search: {
        ...search,
        // Default mode kept out of the URL so the canonical view has a clean URL.
        mode: nextMode === 'material' ? undefined : nextMode,
        productCategory: undefined,
        materialGroupCode: undefined,
        brand: undefined,
        variant: undefined,
        packContentSize: undefined,
        productType: undefined,
        marking: undefined,
        status: undefined,
        page: 1,
        sort: undefined,
      },
    })
  }

  const showResults = () => {
    navigate({
      search: {
        ...search,
        ...selectedFilters,
        keyword: keyword || undefined,
        page: 1,
      },
    })
  }

  const resetAllFilters = () => {
    setKeyword('')
    clearAllFilterState()

    const clearedFilters = getClearedFilters(selectedFilters)

    navigate({
      search: {
        ...search,
        ...clearedFilters,
        keyword: undefined,
        page: 1,
      },
    })
  }

  return (
    <PageContainer>
      <Spread>
        <PageHeader>{t('volumeForecastsTable.pageHeader')}</PageHeader>
        <HStack gap="sm">
          <ExportCsvButton
            label={t('export.viewData', { ns: 'common' })}
            getCsvExport={() =>
              queryClient.ensureQueryData(volumeForecastsExportQueryOptions(volumeForecastsSearch))
            }
            rowCount={rowCount}
            canExceedRowCountLimit
          />
          <VolumeForecastsConfigurationMenu />
        </HStack>
      </Spread>

      <VStack gap="sm">
        {materialsMissingProvincialVolumesCount > 0 && (
          <Note
            tone="warning"
            icon={AlertTriangleIcon}
            note={t('volumeForecastsTable.notes.materialsMissingProvincialVolumes.banner', {
              count: materialsMissingProvincialVolumesCount,
            })}
            action={
              <PermissionsGuard required={['crt_volume_forecasts_configure']}>
                <Button type="button" variant="secondary" size="sm" asChild>
                  <Link to="/crt/volume-forecasts/nc-provincial-contributions">
                    {t('volumeForecastsTable.notes.materialsMissingProvincialVolumes.link')}
                    <ExternalLinkIcon className="size-4" />
                  </Link>
                </Button>
              </PermissionsGuard>
            }
          />
        )}
        {incompleteUnreleasedMaterialsCount > 0 && (
          <Note
            tone="warning"
            icon={AlertTriangleIcon}
            note={t('volumeForecastsTable.notes.incompleteUnreleasedMaterials.banner', {
              count: incompleteUnreleasedMaterialsCount,
            })}
            action={
              <PermissionsGuard required={['crt_unreleased_materials_view']}>
                <Button type="button" variant="secondary" size="sm" asChild>
                  <Link to="/crt/materials/insights/unreleased-materials">
                    {t('volumeForecastsTable.notes.incompleteUnreleasedMaterials.link')}
                    <ExternalLinkIcon className="size-4" />
                  </Link>
                </Button>
              </PermissionsGuard>
            }
          />
        )}
      </VStack>

      <Card>
        <CardContent className="pt-6">
          {showCacheState ? (
            <VolumeForecastsCacheState
              crtPeriod={selectedCrtPeriodStr}
              status={cacheStatus.status}
              error={cacheStatus.error}
              isStale={cacheStatus.isStale}
            />
          ) : (
            <VolumeForecastsDataTable
              mode={mode}
              crtPeriod={selectedCrtPeriodStr}
              selectedPeriod={selectedPeriod}
              filters={filters as Filter<unknown>[]}
              search={{
                className: 'w-96',
                value: keyword,
                setValue: setKeyword,
                placeholder: t('volumeForecastsTable.searchPlaceholder'),
              }}
              actions={
                <HStack gap="xs">
                  <Select
                    value={selectedMonth === INDETERMINATE ? INDETERMINATE : String(selectedMonth)}
                    onValueChange={handleMonthChange}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder={t('volumeForecastsTable.selectMonth')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={INDETERMINATE}>
                        {t('volumeForecastsTable.allMonths')}
                      </SelectItem>
                      <Separator />
                      {MONTH_KEYS.map((monthKey, index) => (
                        <SelectItem key={monthKey} value={String(index + 1)} className="capitalize">
                          {formatMonth(new Date(now.getFullYear(), index, 1))}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={selectedYear} onValueChange={handleYearChange}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder={t('volumeForecastsTable.selectYear')} />
                    </SelectTrigger>
                    <SelectContent>
                      {YEAR_SUFFIXES.map((year) => (
                        <SelectItem key={year} value={year}>
                          {t(`volumeForecastsTable.year.${year}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={mode} onValueChange={handleModeChange}>
                    <SelectTrigger className="w-48 ">
                      <HStack gap="sm">
                        <span className="text-muted-foreground">
                          {t('volumeForecastsTable.viewLabel')}
                        </span>
                        <SelectValue placeholder={t('volumeForecastsTable.selectMode')} />
                      </HStack>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="material">
                        {t('volumeForecastsTable.modes.material')}
                      </SelectItem>
                      <SelectItem value="fmc">{t('volumeForecastsTable.modes.fmc')}</SelectItem>
                      <SelectItem value="vapour">
                        {t('volumeForecastsTable.modes.vapour')}
                      </SelectItem>
                      <SelectItem value="nrt">{t('volumeForecastsTable.modes.nrt')}</SelectItem>
                      <SelectItem value="materialGroup">
                        {t('volumeForecastsTable.modes.materialGroup')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </HStack>
              }
              pageSize={pageSize}
              onPageSizeChange={onPageSizeChange}
              volumeForecastsQuery={volumeForecastsQuery}
              onShowResults={showResults}
              onResetAllFilters={resetAllFilters}
            />
          )}
        </CardContent>
      </Card>
    </PageContainer>
  )
}
